import { Injectable } from '@angular/core';

// Browsers other than Safari can't render HEIC/HEIF, so iPhone photos stored in that format
// show as broken images. We detect HEIC by content (not extension — files often arrive
// mislabeled as .jpg) and transcode to JPEG in the browser so previews render everywhere.
const HEIF_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs', 'heif', 'mif1', 'msf1']);
const HEADER_BYTES = 12;

@Injectable({ providedIn: 'root' })
export class ImageConversionService {
  async toViewableUrl(url: string): Promise<string> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        return url;
      }
      const blob = await response.blob();
      if (!(await this.isHeic(blob))) {
        return url;
      }
      const jpeg = await this.convertHeicToJpeg(blob);
      return URL.createObjectURL(jpeg);
    } catch {
      // Any failure (fetch, CORS, decode) falls back to the original url so viewing is never worse than before.
      return url;
    }
  }

  async isHeic(blob: Blob): Promise<boolean> {
    let header: Uint8Array;
    try {
      header = await this.readHeader(blob);
    } catch {
      return false;
    }
    if (header.length < HEADER_BYTES) {
      return false;
    }
    const tag = (start: number): string => String.fromCharCode(...header.subarray(start, start + 4));
    return tag(4) === 'ftyp' && HEIF_BRANDS.has(tag(8));
  }

  // Isolated so tests can stub the heavy WASM decode; lazy import keeps heic2any out of the main bundle.
  protected async convertHeicToJpeg(blob: Blob): Promise<Blob> {
    const heic2any = (await import('heic2any')).default;
    const converted = await heic2any({ blob, toType: 'image/jpeg' });
    return Array.isArray(converted) ? converted[0] : converted;
  }

  private readHeader(blob: Blob): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (): void => resolve(new Uint8Array(reader.result as ArrayBuffer));
      reader.onerror = (): void => reject(reader.error);
      reader.readAsArrayBuffer(blob.slice(0, HEADER_BYTES));
    });
  }
}
